import React, { useEffect } from 'react'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate } from 'react-router'
import { getCognitoAuthGrant, msRedirect } from 'src/Utils/oidc'
import Cookies from 'js-cookie'
import { COOKIES } from 'src/Utils/globalEnums'
import { useAnalytics } from 'src/analytics/useAnalytics'
import { DirectRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { LoginProviders } from '../Login/LoginPage.constants'
import { LoginPageContainer } from '../Login/LoginPage.styles'
import {
  Auth0Icon,
  EntraIdIcon,
  GoogleIcon,
  LoginButton,
  MicrosoftIcon,
  OktaIcon,
  OneLoginIcon,
} from '../Login/Login.components'
import { StartFlow } from '../Login/EmailLogin/EmailLogin.styles'
import { WhiteTrusteroLabsLogo } from '../ReportScan/ReportScan.components'
import {
  ButtonContainer,
  SignupFormContainer,
  StyledHeaderContainer,
  StyledLineContent,
  StyledLink,
  StyledSubHeader,
  StyledText,
} from './SignUpPage.styles'
import { SignUpProps } from './SignUpPage.constants'

export const SignUpPage = ({
  header,
  subHeader,
  description,
  signupType,
}: SignUpProps): JSX.Element => {
  const navigate = useNavigate()
  const { track, events } = useAnalytics()

  useEffect(() => {
    // This is used to determine if a user logged in from the signup page
    // This allows us to open a welcome back modal for the user
    Cookies.set(COOKIES.FROM_SIGNUP, 'true')
  }, [])

  // This will tell us that the user clicked to go back to the `trustero.com` website
  const fireWebsiteEvent = () => track(events.C_SIGNUP_WEBSITE, { signupType })

  const handleLoginRedirect = () => {
    // This will tell us if the user clicks to go to the '/login' page
    track(events.C_SIGNUP_LOGIN, { signupType })
    navigate('/login')
  }

  const trackOIDC = (provider: string) =>
    track(events.C_SIGNUP_OIDC, { provider, signupType })

  const isQuestionnaire = location.href.includes(
    DirectRoutes.SECURITY_QUESTIONNAIRE_SIGNUP,
  )

  return (
    <LoginPageContainer>
      <SignupFormContainer $isLargeContainer={isQuestionnaire}>
        <StyledHeaderContainer>
          <StyledLink onClick={fireWebsiteEvent} href="https://trustero.com/">
            <FontAwesomeIcon icon={faArrowLeft} />
            Trustero Website
          </StyledLink>
          <WhiteTrusteroLabsLogo />
          <h1>{header}</h1>
        </StyledHeaderContainer>
        <StyledSubHeader>{subHeader}</StyledSubHeader>
        <StyledText>{description}</StyledText>
        <ButtonContainer>
          <LoginButton
            leftIcon={<GoogleIcon />}
            onClickFunc={() =>
              getCognitoAuthGrant({
                provider: LoginProviders.GOOGLE,
                isSignup: true,
                track: trackOIDC.bind(LoginProviders.GOOGLE),
                signupType,
              })
            }
            providerName="Google"
            isSignUp
          />
          <LoginButton
            leftIcon={<Auth0Icon />}
            onClickFunc={() =>
              getCognitoAuthGrant({
                provider: LoginProviders.OKTA, // Auth0 & Okta are the same now, but users may not know that
                isSignup: true,
                track: trackOIDC.bind(LoginProviders.AUTH0),
                signupType,
              })
            }
            providerName="Auth0"
            isSignUp
          />
          <LoginButton
            leftIcon={<OktaIcon />}
            onClickFunc={() =>
              getCognitoAuthGrant({
                provider: LoginProviders.OKTA,
                isSignup: true,
                track: trackOIDC.bind(LoginProviders.OKTA),
                signupType,
              })
            }
            providerName="Okta"
            isSignUp
          />
          <LoginButton
            leftIcon={<OneLoginIcon />}
            onClickFunc={() =>
              getCognitoAuthGrant({
                provider: LoginProviders.ONELOGIN,
                isSignup: true,
                track: trackOIDC.bind(LoginProviders.ONELOGIN),
                signupType,
              })
            }
            providerName="OneLogin"
            isSignUp
          />
          <LoginButton
            leftIcon={<MicrosoftIcon />}
            onClickFunc={() =>
              msRedirect(false, signupType, () =>
                trackOIDC(LoginProviders.MICROSOFT),
              )
            }
            providerName="Microsoft"
            isSignUp
          />
          <LoginButton
            leftIcon={<EntraIdIcon />}
            onClickFunc={() =>
              msRedirect(true, signupType, () =>
                trackOIDC(LoginProviders.ENTRA),
              )
            }
            providerName="Microsoft Entra ID"
            isSignUp
          />
        </ButtonContainer>
        <StyledLineContent>or</StyledLineContent>
        <StartFlow onClick={handleLoginRedirect}>
          Login to your account
        </StartFlow>
      </SignupFormContainer>
    </LoginPageContainer>
  )
}
